"use client";

import React from "react";
import { ArrowLeft, Calendar, Tag, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";


declare const wvcClient: any;

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 bg-muted/30 rounded-md animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((p, i) => (
        <Card key={p.id} data-index={i} className="flex flex-col h-full overflow-hidden border-border/50 shadow-md hover:shadow-lg transition-shadow bg-card">
          {p.featuredImage ? (
            <div className="w-full h-48 overflow-hidden">
              <img
                src={p.featuredImage}
                alt={p.title}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
          ) : (
            <div className="w-full h-48 bg-muted flex items-center justify-center">
              <Tag className="w-8 h-8 text-muted-foreground/50" />
            </div>
          )}
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-default line-clamp-2 leading-tight text-foreground">
              {p.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow flex flex-col justify-between gap-4">
            <p className="text-sm text-muted-foreground line-clamp-3 font-default">
              {p.excerpt?.replace(/<[^>]*>?/gm, "") || "Explore more insights on AI and cloud computing from David's latest research."}
            </p>
            <Link to={p.link || "#"} className="mt-auto">
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
                Read Insight
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId }: { currentPostId: string }) {
  
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 3,
    exclude: [currentPostId],
  });

  return (
    <div className="mt-24 pt-16 border-t border-border">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-bold font-default text-foreground tracking-tight">
          Further Tech Insights
        </h2>
        <Link to="/LibraryPage">
          <Button variant="ghost" className="hidden md:flex text-secondary hover:text-secondary/80">
            Explore The Library
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsGrid />
      </PostsProvider>
    </div>
  );
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-12 animate-pulse">
        <div className="w-24 h-8 bg-muted rounded-md mb-8" />
        <div className="h-12 md:h-16 bg-muted rounded-md w-4/5 mb-6" />
        <div className="flex gap-4 mb-10">
          <div className="h-6 bg-muted rounded-md w-32" />
          <div className="h-6 bg-muted rounded-md w-32" />
        </div>
        <div className="w-full aspect-[21/9] bg-muted rounded-md mb-12" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-md w-full" />
          <div className="h-4 bg-muted rounded-md w-full" />
          <div className="h-4 bg-muted rounded-md w-5/6" />
          <div className="h-4 bg-muted rounded-md w-full mt-8" />
          <div className="h-4 bg-muted rounded-md w-4/5" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-24 text-center">
        <h2 className="text-2xl font-bold text-destructive mb-4 font-default">Error Loading Publication</h2>
        <p className="text-muted-foreground mb-8 font-default">{error}</p>
        <Link to="/HomePage">
          <Button className="rounded-md">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-24 text-center">
        <h2 className="text-2xl font-bold text-foreground mb-4 font-default">Publication Not Found</h2>
        <p className="text-muted-foreground mb-8 font-default">The technical article you are looking for is currently unavailable.</p>
        <Link to="/HomePage">
          <Button className="rounded-md">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 max-w-4xl py-12 md:py-20">
      {}
      <div className="mb-8">
        <Link to="/LibraryPage" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors font-default">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Library
        </Link>
      </div>

      {}
      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="inline-flex items-center px-3 py-1 rounded-md bg-secondary/10 text-secondary text-xs font-bold tracking-widest uppercase font-default">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight mb-6 tracking-tighter">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground font-default">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8 border border-border rounded-md">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback className="rounded-md"><User className="w-4 h-4" /></AvatarFallback>
              </Avatar>
              <span className="font-semibold text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-12 md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full aspect-[16/9] md:aspect-[21/9] object-cover rounded-md shadow-xl border border-border/50"
          />
        </figure>
      )}

      {}
      <div 
        className="prose prose-lg md:prose-xl max-w-none text-foreground/90 font-default leading-relaxed prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-headings:tracking-tight prose-a:text-primary prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-img:rounded-md prose-img:shadow-lg prose-blockquote:border-l-secondary prose-blockquote:bg-muted/30 prose-blockquote:py-4 prose-blockquote:px-8 prose-blockquote:rounded-md prose-blockquote:not-italic prose-blockquote:text-foreground mb-16"
        dangerouslySetInnerHTML={{ __html: post.content || "<p>Technical analysis content is being finalized.</p>" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-16 pt-8 border-t border-border/50">
          <Tag className="w-5 h-5 text-muted-foreground mr-2" />
          {post.tags.map((tag, i) => (
            <Link key={tag.id} data-index={i} to={tag.link || "#"}>
              <span className="inline-flex items-center px-3 py-1 rounded-md bg-muted text-muted-foreground text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors font-default">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {}
      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-8 bg-card rounded-md border border-border shadow-md mb-8">
          <Avatar className="w-24 h-24 border-2 border-primary/20 shadow-sm rounded-md">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl bg-primary/10 text-primary rounded-md">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-xl font-bold font-default text-foreground mb-2">
              Expert Analysis by {post.author.name}
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-4 font-default">
              {post.author.slug 
                ? `David Scott Linthicum is a world-renowned cloud computing and AI expert, author, and consultant helping organizations navigate the complexities of modern technology architecture.` 
                : "SME Contributor"}
            </p>
            {post.author.link && (
              <Link to={post.author.link}>
                <Button variant="outline" size="sm" className="rounded-md border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
                  View All Publications by {post.author.name}
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {}
      <RelatedPosts currentPostId={post.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="240337" id="single-post-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}